/**
 * Shopping lists hooks — TanStack Query wrappers around `ShoppingListsApi`.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type {
  AddShoppingListItemRequest,
  CreateShoppingListRequest,
  UpdateShoppingListItemRequest,
  UpdateShoppingListRequest,
} from '../../types/requests';
import type { IStorage } from '@shopista/storage/interface';
import type {
  ActiveShoppingListResponse,
  HouseholdProduct,
  ShoppingList,
  ShoppingListItem,
  ShoppingListItemWithDetails,
  ShoppingListsResponse,
} from '../../types';
import { useApi, useReadPathContext, useStorage, type ApiBundle } from './context';
import { keys } from './keys';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

function withDisplayFields(
  item: ShoppingListItem,
  list: ShoppingList,
  product: HouseholdProduct | undefined,
): ShoppingListItemWithDetails {
  return {
    ...item,
    list_name: list.name,
    household_id: list.household_id,
    product_name: product?.display_name ?? null,
    product_default_quantity: product?.default_quantity ?? null,
    product_measurement_pack_count: product?.measurement_pack_count ?? null,
    product_measurement_value: product?.measurement_value ?? null,
    product_measurement_unit: product?.measurement_unit ?? null,
    product_measurement_dimension: product?.measurement_dimension ?? null,
    product_measurement_forms: product?.measurement_forms ?? null,
    product_expected_price: product?.expected_price_value ?? null,
    product_expected_price_currency: product?.expected_price_currency ?? null,
    product_expected_price_notes: product?.expected_price_notes ?? null,
    product_notes: product?.notes ?? null,
    display_title: product?.display_name ?? 'Unknown product',
  };
}

export async function readShoppingLists(storage: IStorage): Promise<ShoppingListsResponse> {
  return { lists: await storage.listLists() };
}

export async function readActiveShoppingList(storage: IStorage): Promise<ActiveShoppingListResponse> {
  const lists = await storage.listLists();
  const list = lists.find((candidate) => candidate.status === 'ACTIVE') ?? null;
  if (!list) return { list: null, items: [], total: 0 };
  const items = await Promise.all(
    (await storage.listItemsByList(list.id)).map(async (item) =>
      withDisplayFields(item, list, await storage.getProduct(item.householdproduct_id)),
    ),
  );
  return { list, items, total: items.length };
}

// `networkMode: 'always'` on the two queries below (not on the shared
// QueryClient — every other query keeps the default `'online'` pausing
// behaviour, which is correct for them). These two resolve from `IStorage`
// (Dexie/Memory) and must run regardless of TanStack's own connectivity
// belief: with the default `networkMode: 'online'`, a query with no cached
// data PAUSES — the queryFn is never even invoked — whenever TanStack thinks
// the device is offline, which starves the Dexie read path BUG69 D3 built
// (including the local-storage branch, which needs the queryFn to run).
// `'always'` hands connectivity handling entirely
// to this queryFn's own `isOnline()` check, which is the correct owner here.
//
// `retryOnMount: false` alongside it: `useActiveShoppingList()` is called
// from more than one component (ShoppingListPage AND the mode view it
// renders once loading clears). Each is a separate TanStack observer of the
// SAME query. Mounting a fresh observer on an already-errored, dataless
// query retries by default (`shouldLoadOnMount`) — which, while still
// offline, fails again, flips the shared query back to `pending`, un-renders
// the mode view (removing its observer), and repeats forever: an infinite
// spinner/content oscillation, not a genuine hang, but indistinguishable
// from one. `retryOnMount: false` leaves the *first* fetch's own retry
// policy (`retry`, above/below) untouched — it only stops a same-query
// remount from re-triggering the network dance once we already know the
// answer. Reconnect recovery still works via `refetchOnWindowFocus`, a
// separate code path `retryOnMount` does not touch.

export function shoppingListsQueryOptions(
  api: ApiBundle,
  storage: IStorage | null,
  isOnline: () => boolean,
): UseQueryOptions<ShoppingListsResponse> {
  return {
    queryKey: keys.ShoppingLists.all,
    networkMode: 'always',
    retryOnMount: false,
    queryFn: async () => {
      if (!storage || isOnline()) return api.ShoppingLists.fetchAll();
      return readShoppingLists(storage);
    },
  };
}

export function activeShoppingListQueryOptions(
  api: ApiBundle,
  storage: IStorage | null,
  isOnline: () => boolean,
): UseQueryOptions<ActiveShoppingListResponse> {
  return {
    queryKey: keys.ShoppingLists.active,
    networkMode: 'always',
    retryOnMount: false,
    queryFn: async () => {
      if (!storage || isOnline()) return api.ShoppingLists.fetchActive();
      return readActiveShoppingList(storage);
    },
  };
}

export function useShoppingLists() {
  const api = useApi();
  const storage = useStorage();
  const readPath = useReadPathContext();
  const isOnline = readPath ? readPath.isOnline : () => true;
  return useQuery(shoppingListsQueryOptions(api, storage, isOnline));
}

export function useActiveShoppingList() {
  const api = useApi();
  const storage = useStorage();
  const readPath = useReadPathContext();
  const isOnline = readPath ? readPath.isOnline : () => true;
  return useQuery(activeShoppingListQueryOptions(api, storage, isOnline));
}

export function useShoppingList(listId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: listId ? keys.ShoppingLists.one(listId) : ['shopping-lists', 'disabled'],
    queryFn: () => api.ShoppingLists.fetch(listId as string),
    enabled: Boolean(listId),
  });
}

// ---------------------------------------------------------------------------
// Mutations — lists
// ---------------------------------------------------------------------------

function invalidateLists(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: keys.ShoppingLists.all });
  void qc.invalidateQueries({ queryKey: keys.ShoppingLists.active });
}

export function useCreateShoppingList() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShoppingListRequest) => api.ShoppingLists.create(input),
    onSuccess: () => invalidateLists(qc),
  });
}

export function useUpdateShoppingList() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { listId: string; input: UpdateShoppingListRequest }) =>
      api.ShoppingLists.update(args.listId, args.input),
    onSuccess: (_, vars) => {
      invalidateLists(qc);
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.one(vars.listId) });
    },
  });
}

export function useDeleteShoppingList() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listId: string) => api.ShoppingLists.delete(listId),
    onSuccess: () => invalidateLists(qc),
  });
}

export function useArchiveShoppingList() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listId: string) => api.ShoppingLists.archive(listId),
    onSuccess: () => invalidateLists(qc),
  });
}

// ---------------------------------------------------------------------------
// Mutations — items
// ---------------------------------------------------------------------------

export function useAddShoppingListItem() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { listId: string; input: AddShoppingListItemRequest }) =>
      api.ShoppingLists.addItem(args.listId, args.input),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.one(vars.listId) });
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.active });
    },
  });
}

export function useAddToActiveList() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddShoppingListItemRequest) => api.ShoppingLists.addToActive(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.active });
    },
  });
}

export function useUpdateShoppingListItem() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { listId: string; itemId: string; input: UpdateShoppingListItemRequest }) =>
      api.ShoppingLists.updateItem(args.listId, args.itemId, args.input),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.one(vars.listId) });
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.active });
    },
  });
}

export function useDeleteShoppingListItem() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { listId: string; itemId: string }) =>
      api.ShoppingLists.deleteItem(args.listId, args.itemId),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.one(vars.listId) });
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.active });
    },
  });
}

export function useCheckShoppingListItem() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { listId: string; itemId: string; checked: boolean }) =>
      api.ShoppingLists.checkItem(args.listId, args.itemId, args.checked),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.one(vars.listId) });
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.active });
    },
  });
}

export function usePurchaseShoppingListItem() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { listId: string; itemId: string; actualPrice?: number }) =>
      api.ShoppingLists.purchaseItem(args.listId, args.itemId, args.actualPrice),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.one(vars.listId) });
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.active });
    },
  });
}

export function useBatchPurchaseShoppingList() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { listId: string; itemIds: string[] }) =>
      api.ShoppingLists.batchPurchase(args.listId, args.itemIds),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.one(vars.listId) });
      void qc.invalidateQueries({ queryKey: keys.ShoppingLists.active });
    },
  });
}
